import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@planone/bau365/util-notification';
import { SnackBarType } from '@planone/shared/util-interfaces';
import { saveAs } from 'file-saver';
import { KeycloakService } from 'keycloak-angular';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import * as ExportActions from './export.actions';
import { ExportMetadata } from './export.models';

import { ExportService } from '../../services/export.service';

@Injectable()
export class ExportEffects {
  download$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportActions.download),
      switchMap((action) =>
        this.exportService.download(action.documentId).pipe(
          map((result) =>
            ExportActions.downloadSucceeded({
              action,
              result,
            })
          ),
          catchError(() => of(ExportActions.downloadFailed(action)))
        )
      )
    )
  );

  downloadSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportActions.downloadSucceeded),
      switchMap(({ action, result }) =>
        this.exportService.identifyMeta(action.documentId).pipe(
          map((meta) =>
            ExportActions.downloadTrigger({
              action: {
                ...action,
                fileName: meta.name ?? undefined,
              },
              result,
            })
          ),
          catchError(() => of(ExportActions.downloadFailed(action)))
        )
      )
    )
  );

  downloadTrigger$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportActions.downloadTrigger),
      map(({ action, result }) => {
        let filename: string;

        if (!action.fileName) {
          const today = new Date().toJSON().slice(0, 10);
          filename = today;

          if (action.exportTypeName) {
            filename += '_' + action.exportTypeName;
          }

          const instance = this.keycloakService.getKeycloakInstance();
          const token = instance.tokenParsed;
          if (token) {
            const initals = token['given_name'].charAt(0).toUpperCase() + token['family_name'].charAt(0).toUpperCase();
            filename += '_' + initals;
          }

          filename += '.xlsx';
        } else {
          filename = action.fileName;
        }

        saveAs(result, filename);

        return ExportActions.trackDownloadSucceeded({
          action: action,
          result: result,
          file_name: filename,
          file_extension: 'xlsx',
        });
      })
    )
  );

  downloadFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ExportActions.downloadFailed),
        tap(() =>
          this.notificationService.projectContent(
            $localize`:@@featureExport-toasts-downloadFailed:Herunterladen fehlgeschlagen`,
            SnackBarType.failure,
            undefined,
            true
          )
        )
      ),
    { dispatch: false }
  );

  forward$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportActions.forward),
      switchMap((action) =>
        action.documentId
          ? this.exportService.identifyMeta(action.documentId).pipe(
              map((meta) =>
                ExportActions.trackForwardSucceeded({
                  action: action.action,
                  file_name: meta.name,
                  file_extension: meta.name?.split('.').pop(),
                })
              ),
              catchError(() => of(ExportActions.metaIdentificationFailed()))
            )
          : of(
              ExportActions.trackForwardSucceeded({
                action: action.action,
                file_name: undefined,
                file_extension: undefined,
              })
            )
      )
    )
  );

  identifyMeta$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportActions.identifyMeta),
      switchMap((action) =>
        this.exportService.identifyMeta(action.documentId).pipe(
          map((result: ExportMetadata) => ExportActions.metaIdentificationSucceeded(result)),
          catchError(() => of(ExportActions.metaIdentificationFailed()))
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private keycloakService: KeycloakService,
    private exportService: ExportService,
    private notificationService: NotificationService
  ) {}
}
